// Shared client library for the LIVE lab pages (see SPEC.md "Client library").
import { Device } from 'mediasoup-client';
import { candidateFamily, pathLabel } from './netfamily.js';

// "UDP · IPv6" style label for a selected pair (see netfamily.js).
export { pathLabel };

export const t = {
  appName: 'مختبر بثّ Showgram',
  missingKey: 'الرابط ناقص. اطلب رابط التجربة من الإدارة.',
  home: 'الرئيسيّة',
  connecting: 'جارٍ الاتّصال بالخادم…',
  syncing: 'جارٍ ضبط الساعة مع الخادم…',
  connected: 'متّصل',
  disconnected: 'انقطع الاتّصال بالخادم.',
  reconnecting: 'انقطع الاتّصال. جارٍ إعادة المحاولة…',
  serverOk: 'الخادم يعمل',
  serverDown: 'الخادم لا يستجيب',
  chooseCarrier: 'اختر شبكتك',
  carrierRequired: 'اختر الشبكة أوّلاً.',
  note: 'ملاحظة (اختياريّة)',
  submit: 'أرسل النتيجة',
  submitting: 'جارٍ الإرسال…',
  submitted: 'أُرسلت النتيجة ✓',
  submitFailed: 'تعذّر إرسال النتيجة. حاول مرّة أخرى.',
  retry: 'أعد المحاولة',
  error: 'حدث خطأ',
  unsupported: 'هذا المتصفّح لا يدعم البثّ عبر WebRTC. جرّب Chrome أو Safari بإصدار حديث.',
  uncertainty: 'دقّة المزامنة',
  errors: {
    bad_request: 'طلب غير صالح.',
    unauthorized: 'الرابط غير صالح. اطلب رابطاً جديداً من الإدارة.',
    rate_limited: 'طلبات كثيرة. انتظر قليلاً ثمّ حاول.',
    not_found: 'غير موجود. ربّما انتهى البثّ.',
    forbidden: 'غير مسموح.',
    room_full: 'البثّ ممتلئ بالمشاهدين.',
    too_many_rooms: 'عدد البثوث المفتوحة بلغ الحدّ. حاول بعد قليل.',
    internal: 'خطأ في الخادم.',
    timeout: 'الخادم لم يردّ في الوقت المحدّد.',
    closed: 'انقطع الاتّصال بالخادم.',
    connect_failed: 'تعذّر الاتّصال بالخادم.',
    no_key: 'الرابط ناقص. اطلب رابط التجربة من الإدارة.',
    unsupported: 'هذا المتصفّح لا يدعم البثّ عبر WebRTC.',
    network: 'تعذّر الوصول إلى الخادم. تحقّق من الإنترنت.',
    too_large: 'حجم البيانات كبير جدّاً.',
    storage_full: 'مساحة النتائج ممتلئة على الخادم.',
  },
};

// Arabic text for an Error thrown by this library (err.message is the code).
export function errorText(err) {
  const code = typeof err === 'string' ? err : err && (err.code || err.message);
  return (code && t.errors[code]) || t.error;
}

// ---------- access key ----------

const KEY_STORE = 'showgramLabKey';
let cachedKey;

function storageGet(store, name) {
  try {
    return globalThis[store] ? globalThis[store].getItem(name) : null;
  } catch {
    return null;
  }
}

function storageSet(store, name, value) {
  try {
    if (globalThis[store]) globalThis[store].setItem(name, value);
  } catch {
    /* private mode or blocked storage */
  }
}

function cleanKey(v) {
  if (typeof v !== 'string') return null;
  const k = v.trim();
  return k.length > 0 && k.length <= 256 ? k : null;
}

export function getKey() {
  if (cachedKey !== undefined) return cachedKey;
  let key = null;
  if (typeof location !== 'undefined') {
    const url = new URL(location.href);
    const fromUrl = cleanKey(url.searchParams.get('k'));
    if (url.searchParams.has('k')) {
      url.searchParams.delete('k');
      try {
        history.replaceState(history.state, '', url.pathname + url.search + url.hash);
      } catch {
        /* ignore */
      }
    }
    if (fromUrl) {
      key = fromUrl;
      storageSet('sessionStorage', KEY_STORE, key);
      storageSet('localStorage', KEY_STORE, key);
    }
  }
  if (!key) {
    key = cleanKey(storageGet('sessionStorage', KEY_STORE)) || cleanKey(storageGet('localStorage', KEY_STORE));
  }
  cachedKey = key;
  return key;
}

export function requireKeyOrExplain(el) {
  if (getKey()) return true;
  const target = el || (typeof document !== 'undefined' && (document.querySelector('main') || document.body));
  if (target) {
    target.textContent = t.missingKey;
    target.classList.remove('hidden');
    if (!target.classList.contains('notice')) target.classList.add('notice');
    target.classList.add('bad');
    target.setAttribute('role', 'alert');
  }
  return false;
}

// Take the key out of the address bar as early as possible.
if (typeof location !== 'undefined' && typeof history !== 'undefined') getKey();

// ---------- WebSocket client ----------

function codedError(code, msg) {
  const e = new Error(code);
  e.code = code;
  if (msg) e.detail = String(msg);
  return e;
}

// Push events come from the server as { p, d }. Local pseudo-event: 'close' ({ code, reason }).
export class Lab {
  #ws;
  #nextId = 1;
  #pending = new Map();
  #listeners = new Map();
  #clock = null;
  #closed = false;

  constructor(ws) {
    this.#ws = ws;
    ws.addEventListener('message', (ev) => this.#onMessage(ev));
    ws.addEventListener('close', (ev) => this.#onClose(ev));
  }

  static connect(timeoutMs = 10000) {
    const key = getKey();
    if (!key) return Promise.reject(codedError('no_key'));
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${proto}//${location.host}/ws?k=${encodeURIComponent(key)}`;
    return new Promise((resolve, reject) => {
      let ws;
      try {
        ws = new WebSocket(url);
      } catch {
        reject(codedError('connect_failed'));
        return;
      }
      let settled = false;
      const timer = setTimeout(() => finish(codedError('timeout')), timeoutMs);
      const finish = (err) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        ws.removeEventListener('open', onOpen);
        ws.removeEventListener('error', onFail);
        ws.removeEventListener('close', onFail);
        if (err) {
          try {
            ws.close();
          } catch {
            /* ignore */
          }
          reject(err);
        } else {
          resolve(new Lab(ws));
        }
      };
      const onOpen = () => finish(null);
      const onFail = () => finish(codedError('connect_failed'));
      ws.addEventListener('open', onOpen);
      ws.addEventListener('error', onFail);
      ws.addEventListener('close', onFail);
    });
  }

  get isOpen() {
    return !this.#closed && this.#ws.readyState === WebSocket.OPEN;
  }

  get clock() {
    if (!this.#clock) this.#clock = new Clock(this);
    return this.#clock;
  }

  request(method, data = {}, timeoutMs = 8000) {
    if (!this.isOpen) return Promise.reject(codedError('closed'));
    const id = this.#nextId++;
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.#pending.delete(id);
        reject(codedError('timeout'));
      }, timeoutMs);
      this.#pending.set(id, { resolve, reject, timer });
      try {
        this.#ws.send(JSON.stringify({ id, m: method, d: data ?? {} }));
      } catch {
        clearTimeout(timer);
        this.#pending.delete(id);
        reject(codedError('closed'));
      }
    });
  }

  on(event, fn) {
    let set = this.#listeners.get(event);
    if (!set) this.#listeners.set(event, (set = new Set()));
    set.add(fn);
    return () => set.delete(fn);
  }

  close() {
    if (this.#closed) return;
    try {
      this.#ws.close(1000, 'bye');
    } catch {
      /* ignore */
    }
    this.#onClose({ code: 1000, reason: 'client' });
  }

  #emit(event, data) {
    const set = this.#listeners.get(event);
    if (!set) return;
    for (const fn of [...set]) {
      try {
        fn(data);
      } catch (err) {
        console.error('lab listener failed', event, err);
      }
    }
  }

  #onMessage(ev) {
    if (typeof ev.data !== 'string') return;
    let msg;
    try {
      msg = JSON.parse(ev.data);
    } catch {
      return;
    }
    if (!msg || typeof msg !== 'object') return;
    if (typeof msg.p === 'string') {
      this.#emit(msg.p, msg.d ?? {});
      return;
    }
    const p = this.#pending.get(msg.id);
    if (!p) return;
    this.#pending.delete(msg.id);
    clearTimeout(p.timer);
    if (msg.ok) p.resolve(msg.d ?? {});
    else p.reject(codedError(typeof msg.e === 'string' ? msg.e : 'internal', msg.msg));
  }

  #onClose(ev) {
    if (this.#closed) return;
    this.#closed = true;
    if (this.#clock) this.#clock.stop();
    for (const p of this.#pending.values()) {
      clearTimeout(p.timer);
      p.reject(codedError('closed'));
    }
    this.#pending.clear();
    this.#emit('close', { code: ev?.code ?? 1006, reason: ev?.reason ?? '' });
  }
}

// ---------- server clock ----------

const wallNow = () => performance.timeOrigin + performance.now();
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
// Wall clock minus the performance clock. It jumps when performance.now() stops while the
// device sleeps (iOS/macOS mach time, Android CLOCK_MONOTONIC) and the wall clock runs on.
const perfDrift = () => Date.now() - wallNow();

const KEEP_AGE_MS = 10 * 60_000; // samples expire by age, not by count
const KEEP_MAX = 240;
// After a full sync, a later sample may move the estimate only if its RTT is close to the
// idle-link RTT of that sync: samples taken under load carry an (up - down) / 2 bias.
const ACCEPT_RTT_FACTOR = 1.2;
const ACCEPT_RTT_SLACK_MS = 3;
const SLEW_MS_PER_S = 0.5; // later corrections are slewed, never stepped
const SUSPEND_JUMP_MS = 50;
const RESYNC_SAMPLES = 5;

export class Clock {
  #lab;
  #samples = []; // { rtt, offset, at: performance.now() at receive, drift }
  #synced = false;
  #baseRtt = Infinity; // best RTT of the last full sync
  #target = 0; // offset the estimate converges to
  #targetRtt = Infinity; // RTT of the sample #target comes from
  #from = 0; // applied offset when #target last changed
  #fromAt = 0; // performance.now() of that change
  #driftRef = null;
  #timer = null;
  #busy = false;
  #resyncing = false;
  #onVisible = null;

  constructor(lab) {
    this.#lab = lab;
  }

  #applied(p = performance.now()) {
    const d = this.#target - this.#from;
    const max = (SLEW_MS_PER_S * Math.max(0, p - this.#fromAt)) / 1000;
    return Math.abs(d) <= max ? this.#target : this.#from + Math.sign(d) * max;
  }

  #setTarget(offset, rtt, jump) {
    const p = performance.now();
    this.#from = jump ? offset : this.#applied(p);
    this.#fromAt = p;
    this.#target = offset;
    this.#targetRtt = rtt;
  }

  #prune() {
    const p = performance.now();
    while (this.#samples.length && (this.#samples.length > KEEP_MAX || p - this.#samples[0].at > KEEP_AGE_MS)) {
      this.#samples.shift();
    }
  }

  async #sample() {
    const d0 = perfDrift();
    const t0 = wallNow();
    const r = await this.#lab.request('time', {}, 4000);
    const t1 = wallNow();
    const d1 = perfDrift();
    const ts = Number(r && r.ts);
    if (!Number.isFinite(ts)) throw codedError('bad_request');
    // The device slept while the request was in flight: the sample is meaningless.
    if (Math.abs(d1 - d0) > SUSPEND_JUMP_MS) throw codedError('suspended');
    const s = { rtt: t1 - t0, offset: ts - (t0 + t1) / 2, at: performance.now(), drift: d1 };
    this.#samples.push(s);
    this.#prune();
    return s;
  }

  // A burst of samples defines the estimate (stepped) and the idle-link RTT baseline.
  async #burst(n) {
    const got = [];
    let lastErr = null;
    for (let i = 0; i < n; i++) {
      if (i > 0) await sleep(60);
      try {
        got.push(await this.#sample());
      } catch (err) {
        lastErr = err;
        if (err && err.code === 'closed') break;
      }
    }
    if (!got.length) throw lastErr || codedError('timeout');
    const lastDrift = got[got.length - 1].drift;
    let best = null;
    for (const s of got) {
      if (Math.abs(s.drift - lastDrift) > SUSPEND_JUMP_MS) continue;
      if (!best || s.rtt < best.rtt) best = s;
    }
    this.#baseRtt = best.rtt;
    this.#setTarget(best.offset, best.rtt, true);
    this.#driftRef = lastDrift;
    this.#synced = true;
  }

  // Periodic samples may only refine the estimate with a near-idle RTT; changes are slewed.
  #recompute() {
    let best = null;
    for (const s of this.#samples) if (!best || s.rtt < best.rtt) best = s;
    if (!best || best.offset === this.#target) return;
    const limit = Math.max(this.#baseRtt * ACCEPT_RTT_FACTOR, this.#baseRtt + ACCEPT_RTT_SLACK_MS);
    if (best.rtt <= limit) this.#setTarget(best.offset, best.rtt, false);
  }

  // Detects a device sleep (performance.now() paused). Shifts the estimate by the gap at
  // once, drops the old samples and, when running, re-measures. Returns true on a jump.
  #checkSuspend() {
    if (!this.#synced || this.#driftRef === null) return false;
    const cur = perfDrift();
    const jump = cur - this.#driftRef;
    this.#driftRef = cur;
    if (Math.abs(jump) <= SUSPEND_JUMP_MS) return false;
    this.#samples = [];
    this.#setTarget(this.#applied() + jump, this.#targetRtt, true);
    if (this.#timer) this.#resync();
    return true;
  }

  #resync() {
    if (this.#resyncing) return;
    this.#resyncing = true;
    this.#burst(RESYNC_SAMPLES)
      .catch(() => { /* keep the shifted estimate until the next sample */ })
      .finally(() => {
        this.#resyncing = false;
      });
  }

  async #tick() {
    if (this.#checkSuspend() || this.#busy || this.#resyncing) return;
    this.#busy = true;
    try {
      await this.#sample();
      this.#recompute();
    } catch {
      /* keep the last good estimate */
    } finally {
      this.#busy = false;
    }
  }

  async syncInitial(samples = 15) {
    await this.#burst(samples);
  }

  start(periodMs = 5000) {
    this.stop();
    this.#timer = setInterval(() => this.#tick(), periodMs);
    if (typeof document !== 'undefined') {
      this.#onVisible = () => {
        if (document.visibilityState === 'visible') this.#checkSuspend();
      };
      document.addEventListener('visibilitychange', this.#onVisible);
    }
  }

  stop() {
    if (this.#timer) clearInterval(this.#timer);
    this.#timer = null;
    if (this.#onVisible && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.#onVisible);
    }
    this.#onVisible = null;
  }

  get synced() {
    return this.#synced;
  }

  now() {
    this.#checkSuspend();
    return wallNow() + this.#applied();
  }

  toServer(perfNowMs) {
    this.#checkSuspend();
    return performance.timeOrigin + perfNowMs + this.#applied();
  }

  get offsetMs() {
    return this.#applied();
  }

  // RTT / 2 of the sample the estimate comes from; Infinity until the first sync.
  get uncertaintyMs() {
    return this.#targetRtt / 2;
  }

  get rttSamples() {
    return this.#samples.map((s) => s.rtt);
  }
}

// ---------- screen wake lock ----------
// Keeps the screen on while a measurement runs; re-acquired whenever the page is visible
// again. `supported` is false where the API is missing (iOS Safari < 16.4).
// Call it from a click handler before the first await.
export function keepScreenOn() {
  const api = typeof navigator !== 'undefined' ? navigator.wakeLock : undefined;
  const handle = { supported: !!(api && typeof api.request === 'function'), release() {} };
  if (!handle.supported || typeof document === 'undefined') return handle;
  let sentinel = null;
  let pending = false;
  let released = false;
  const acquire = () => {
    if (released || sentinel || pending || document.visibilityState !== 'visible') return;
    pending = true;
    api.request('screen').then(
      (s) => {
        pending = false;
        if (released) {
          s.release().catch(() => {});
          return;
        }
        sentinel = s;
        s.addEventListener('release', () => {
          if (sentinel === s) sentinel = null;
        });
      },
      () => {
        pending = false;
      },
    );
  };
  const onVisibility = () => {
    if (document.visibilityState === 'visible') acquire();
  };
  document.addEventListener('visibilitychange', onVisibility);
  acquire();
  handle.release = () => {
    if (released) return;
    released = true;
    document.removeEventListener('visibilitychange', onVisibility);
    const s = sentinel;
    sentinel = null;
    if (s) s.release().catch(() => {});
  };
  return handle;
}

// Baghdad wall clock (UTC+3, no DST) as HH:MM:SS.mmm.
export function fmtClock(epochMs) {
  if (typeof epochMs !== 'number' || !Number.isFinite(epochMs)) return '--:--:--.---';
  const d = new Date(Math.floor(epochMs) + 3 * 3600 * 1000);
  const p2 = (n) => String(n).padStart(2, '0');
  return `${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())}:${p2(d.getUTCSeconds())}.${String(d.getUTCMilliseconds()).padStart(3, '0')}`;
}

// ---------- mediasoup / WebRTC helpers ----------

export async function loadDevice(routerRtpCapabilities) {
  let device;
  try {
    device = await Device.factory();
  } catch {
    try {
      device = new Device();
    } catch (err) {
      const e = codedError('unsupported', err && err.message);
      throw e;
    }
  }
  await device.load({ routerRtpCapabilities });
  return device;
}

function statsList(report) {
  const list = [];
  if (!report) return list;
  if (typeof report.forEach === 'function') report.forEach((s) => list.push(s));
  else if (typeof report === 'object') for (const k of Object.keys(report)) list.push(report[k]);
  return list;
}

// family: 'ipv4' | 'ipv6' | null from the REMOTE candidate of the selected pair (our announced
// address; `address`, or `ip` in older Chrome/Safari). A pair never mixes families, so a local
// candidate with a literal IP is used when the remote one has no address.
export async function selectedPairInfo(transport) {
  const none = { protocol: null, family: null, localType: null, remoteType: null, rttMs: null };
  let report;
  try {
    report = await transport.getStats();
  } catch {
    return none;
  }
  const list = statsList(report);
  const byId = new Map(list.filter((s) => s && s.id).map((s) => [s.id, s]));

  let pair = null;
  for (const s of list) {
    if (s && s.type === 'transport' && s.selectedCandidatePairId && byId.has(s.selectedCandidatePairId)) {
      pair = byId.get(s.selectedCandidatePairId);
      break;
    }
  }
  if (!pair) {
    const pairs = list.filter((s) => s && s.type === 'candidate-pair');
    const score = (p) => (p.bytesReceived || 0) + (p.bytesSent || 0);
    const pick = (arr) => arr.sort((a, b) => score(b) - score(a))[0] || null;
    pair =
      pick(pairs.filter((p) => p.selected === true)) ||
      pick(pairs.filter((p) => p.nominated && p.state === 'succeeded')) ||
      pick(pairs.filter((p) => p.state === 'succeeded'));
  }
  if (!pair) return none;

  const local = byId.get(pair.localCandidateId) || null;
  const remote = byId.get(pair.remoteCandidateId) || null;
  const rawProto = String((local && local.protocol) || (remote && remote.protocol) || '').toLowerCase();
  const protocol = rawProto === 'udp' || rawProto === 'tcp' ? rawProto : null;

  let rttMs = null;
  if (typeof pair.currentRoundTripTime === 'number' && Number.isFinite(pair.currentRoundTripTime)) {
    rttMs = pair.currentRoundTripTime * 1000;
  } else if (pair.totalRoundTripTime > 0 && pair.responsesReceived > 0) {
    rttMs = (pair.totalRoundTripTime / pair.responsesReceived) * 1000;
  }

  return {
    protocol,
    family: candidateFamily(remote) ?? candidateFamily(local),
    localType: (local && local.candidateType) || null,
    remoteType: (remote && remote.candidateType) || null,
    rttMs,
  };
}

export function detectPlatform() {
  const nav = typeof navigator !== 'undefined' ? navigator : {};
  const ua = String(nav.userAgent || '');
  let os = 'other';
  if (/iPhone|iPad|iPod/i.test(ua) || (/Macintosh/.test(ua) && nav.maxTouchPoints > 1)) os = 'ios';
  else if (/Android/i.test(ua)) os = 'android';

  const tests = [
    ['instagram', /Instagram/i],
    ['facebook', /FBAN|FBAV|FB_IAB/],
    ['tiktok', /musical_ly|BytedanceWebview|TikTok/i],
    ['snapchat', /Snapchat/i],
    ['telegram', /Telegram/i],
    ['samsung', /SamsungBrowser\/(\d+)/],
    ['edge', /Edg(?:A|iOS)?\/(\d+)/],
    ['opera', /OPR\/(\d+)|OPT\/(\d+)/],
    ['firefox', /(?:Firefox|FxiOS)\/(\d+)/],
    ['chrome', /(?:Chrome|CriOS)\/(\d+)/],
    ['safari', /Version\/(\d+)[\d.]*.*Safari/],
  ];
  let browser = 'other';
  for (const [name, re] of tests) {
    const m = re.exec(ua);
    if (!m) continue;
    const ver = m.slice(1).find(Boolean);
    browser = ver ? `${name} ${ver}` : name;
    break;
  }
  if (browser === 'other' && os === 'ios' && /AppleWebKit/.test(ua)) browser = 'webview';
  if (os === 'android' && /; wv\)/.test(ua) && !/^(instagram|facebook|tiktok|snapchat|telegram)/.test(browser)) {
    browser = `webview ${browser}`.trim();
  }
  return { os, browser: browser.slice(0, 40), ua };
}

function effectiveType() {
  try {
    const c = navigator.connection;
    return c && typeof c.effectiveType === 'string' ? c.effectiveType : undefined;
  } catch {
    return undefined;
  }
}

export async function submitResult(kind, payload) {
  const key = getKey();
  if (!key) throw codedError('no_key');
  const body = { ...(payload || {}), kind };
  const plat = body.platform && typeof body.platform === 'object' ? body.platform : detectPlatform();
  body.platform = { os: plat.os, browser: plat.browser };
  if (body.effectiveType === undefined) {
    const et = effectiveType();
    if (et) body.effectiveType = et;
  }

  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), 15000) : null;
  let res;
  try {
    res = await fetch('/api/results', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Lab-Key': key },
      body: JSON.stringify(body),
      keepalive: true,
      cache: 'no-store',
      credentials: 'same-origin',
      signal: ctrl ? ctrl.signal : undefined,
    });
  } catch (err) {
    throw codedError(err && err.name === 'AbortError' ? 'timeout' : 'network');
  } finally {
    if (timer) clearTimeout(timer);
  }
  if (!res.ok) {
    let code = null;
    try {
      const j = await res.json();
      if (j && typeof j.e === 'string') code = j.e;
      else if (j && typeof j.error === 'string') code = j.error;
    } catch {
      /* not JSON */
    }
    if (!code) {
      code =
        res.status === 401 ? 'unauthorized'
        : res.status === 413 ? 'too_large'
        : res.status === 429 ? 'rate_limited'
        : res.status === 507 ? 'storage_full'
        : res.status === 400 ? 'bad_request'
        : 'internal';
    }
    throw codedError(code, `HTTP ${res.status}`);
  }
}

// ---------- formatting ----------
// Latin digits with a unit. The result is wrapped in an LTR isolate (U+2066..U+2069) so
// "12 ms" or "-30 ms" keeps its order inside Arabic sentences. Non-numbers give '—'.

function isNum(x) {
  return typeof x === 'number' && Number.isFinite(x);
}

function ltr(s) {
  return `⁦${s}⁩`;
}

export function fmtMs(x) {
  if (!isNum(x)) return '—';
  const a = Math.abs(x);
  let s = a < 10 && a !== 0 ? x.toFixed(1) : String(Math.round(x));
  if (s === '-0' || s === '-0.0' || s === '0.0') s = '0';
  return ltr(`${s} ms`);
}

export function fmtMbps(x) {
  if (!isNum(x)) return '—';
  const a = Math.abs(x);
  const s = a >= 100 ? String(Math.round(x)) : a >= 10 ? x.toFixed(1) : x.toFixed(2);
  return ltr(`${s} Mbps`);
}

// x is already a percentage (0..100), not a fraction.
export function fmtPct(x) {
  if (!isNum(x)) return '—';
  const a = Math.abs(x);
  const s = a === 0 ? '0' : a >= 10 ? String(Math.round(x)) : a >= 1 ? x.toFixed(1) : x.toFixed(2);
  return ltr(`${s}%`);
}

// ---------- carriers ----------

export const CARRIERS = [
  { id: 'asiacell', label: 'آسياسيل' },
  { id: 'zain', label: 'زين' },
  { id: 'korek', label: 'كورك' },
  { id: 'earthlink', label: 'إيرثلنك' },
  { id: 'wifi_other', label: 'واي فاي آخر' },
  { id: 'other', label: 'غير ذلك' },
];

const CARRIER_STORE = 'showgramLabCarrier';
let pickerSeq = 0;

export function carrierPicker(container, onChange) {
  if (!container) throw new TypeError('carrierPicker needs a container element');
  const ids = new Set(CARRIERS.map((c) => c.id));
  const saved = storageGet('localStorage', CARRIER_STORE);
  const initial = saved && ids.has(saved) ? saved : null;
  const uid = `carrier-${++pickerSeq}`;

  const fs = document.createElement('fieldset');
  fs.className = 'carrier-picker';
  const legend = document.createElement('legend');
  legend.textContent = t.chooseCarrier;
  const grid = document.createElement('div');
  fs.append(legend, grid);

  const inputs = [];
  for (const c of CARRIERS) {
    const label = document.createElement('label');
    const input = document.createElement('input');
    input.type = 'radio';
    input.name = 'carrier';
    input.value = c.id;
    input.id = `${uid}-${c.id}`;
    if (c.id === initial) input.checked = true;
    const span = document.createElement('span');
    span.textContent = c.label;
    label.append(input, span);
    grid.appendChild(label);
    inputs.push(input);
  }

  const current = () => {
    const el = inputs.find((i) => i.checked);
    return el ? el.value : null;
  };

  fs.addEventListener('change', () => {
    const v = current();
    if (v) storageSet('localStorage', CARRIER_STORE, v);
    if (typeof onChange === 'function') onChange(v);
  });

  container.replaceChildren(fs);
  if (initial && typeof onChange === 'function') queueMicrotask(() => onChange(initial));

  return {
    get value() {
      return current();
    },
    set value(v) {
      for (const i of inputs) i.checked = i.value === v;
    },
    el: fs,
  };
}
